import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SortOrder, listFiltersService, ListTableSort, depascalize } from "sprint-common-web";

import { TrailerFilter } from "../../../../common/components/trailer-info/TrailerFilter";
import { CloudAssetOrigin } from "../../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../../common/types/AssetStatus";
import { AssetType } from "../../../../common/types/AssetType";
import { RecordType } from "../../../../common/types/RecordType";

export interface MovieAssetsListUrlFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    statusIds?: number[];
    distributorIds?: string[];
    versionTypeIds?: string[];
    types?: number[];
    recordTypes?: number[];
    origins?: number[];
    createdDateFrom?: Date;
    createdDateTo?: Date;
    updatedDateFrom?: Date;
    updatedDateTo?: Date;
    trailers?: number[];
}

const statuses = [AssetStatus.Active];

const selectedTrailers = [TrailerFilter.Trailer, TrailerFilter.NonTrailer];

const defaultFilters: MovieAssetsListUrlFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "updatedAudit",
    sortOrder: "desc",
    statusIds: statuses,
    distributorIds: [],
    versionTypeIds: [],
    types: [AssetType.Cloud, AssetType.OnPrem],
    recordTypes: [RecordType.Video, RecordType.Audio, RecordType.Subtitle],
    origins: [CloudAssetOrigin.User, CloudAssetOrigin.System],
    trailers: selectedTrailers
};

export const useMovieAssetsListUrlFilters = () => {
    const filtersService = useMemo(() => listFiltersService<MovieAssetsListUrlFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<MovieAssetsListUrlFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [filters]);

    const setSort = ({ by: sortBy, order: sortOrder }: ListTableSort) => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, sortBy, sortOrder, page: 1 }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setStatusIds = (statusIds: number[]): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, statusIds, page: 1 }));
    };

    const setDistributorIds = (distributorIds: string[] | undefined): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, distributorIds, page: 1 }));
    };

    const setVersionTypeIds = (versionTypeIds: string[] | undefined): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, versionTypeIds, page: 1 }));
    };

    const setAssetTypes = (types?: number[]): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, types, page: 1 }));
    };

    const setAssetRecordTypes = (recordTypes?: number[]): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, recordTypes, page: 1 }));
    };

    const setAssetOrigins = (origins?: number[]): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, origins, page: 1 }));
    };

    const setTrailers = (trailers?: number[]): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({
            ...prevFilters,
            trailers,
            page: 1
        }));
    };

    const setCreatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, createdDateFrom: date, page: 1 }));
    };

    const setCreatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, createdDateTo: date, page: 1 }));
    };

    const setUpdatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, updatedDateFrom: date, page: 1 }));
    };

    const setUpdatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: MovieAssetsListUrlFilters) => ({ ...prevFilters, updatedDateTo: date, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const trailersOptions = selectedTrailers.map((type) => ({
        key: type,
        text: depascalize(TrailerFilter[type])
    }));

    return {
        filters,
        statuses,
        trailersOptions,
        setTrailers,
        setStatusIds,
        setSort,
        setPage,
        setPageSize,
        clearFilters,
        setDistributorIds,
        setVersionTypeIds,
        setCreatedDateFrom,
        setCreatedDateTo,
        setUpdatedDateFrom,
        setUpdatedDateTo,
        setAssetTypes,
        setAssetRecordTypes,
        setAssetOrigins
    };
};
